import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ChevronLeft, Users, Package, Bookmark, Mail, KeyRound, RefreshCw,
  ShieldOff, ShieldCheck, ChevronRight, CheckCircle2, Clock, Trash2,
} from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import PlanBadge from "@/components/PlanBadge";
import IdChip from "@/components/IdChip";

/**
 * Super-Admin Club Detail — full drill-in view of a single tenant.
 *
 * Master-only page. Shows the club's profile, intelligence (top brands,
 * categories, vendors, order-status breakdown), full member roster with
 * activation status, and the last 25 orders. Lets the master rotate the
 * back-office passcode, edit profile fields, and re-fire individual member
 * activation invites without leaving the tenant context.
 */
export default function SuperAdminClubDetailPage() {
  const { clubId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState("profile");

  // Action UI state
  const [rotateOpen, setRotateOpen] = useState(false);
  const [newPasscode, setNewPasscode] = useState("");
  const [rotateBusy, setRotateBusy] = useState(false);
  const [rotateResult, setRotateResult] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [editBusy, setEditBusy] = useState(false);
  const [resendingId, setResendingId] = useState(null);
  const [statusBusy, setStatusBusy] = useState(false);

  const reload = () => {
    setLoading(true);
    setError(null);
    api
      .get(`/super-admin/clubs/${clubId}`)
      .then((r) => {
        setData(r.data);
        setEditForm({
          name: r.data.club.name || "",
          city: r.data.club.city || "",
          contact_email: r.data.club.contact_email || "",
          phone: r.data.club.phone || "",
          address: r.data.club.address || "",
          website: r.data.club.website || "",
          established_year: r.data.club.established_year || "",
          auto_approve_members: !!r.data.club.auto_approve_members,
          public_about: r.data.club.public_about || "",
        });
      })
      .catch((err) => setError(formatApiErrorDetail(err.response?.data?.detail) || err.message))
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { reload(); }, [clubId]);

  const rotatePasscode = async () => {
    if (!newPasscode || newPasscode.length < 5 || newPasscode.length > 10) {
      setError("Passcode must be 5–10 characters.");
      return;
    }
    setRotateBusy(true);
    setError(null);
    try {
      const { data: r } = await api.post(`/super-admin/clubs/${clubId}/rotate-passcode`, { passcode: newPasscode });
      setRotateResult(r.passcode);
      setNewPasscode("");
      reload();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setRotateBusy(false);
    }
  };

  const saveProfile = async () => {
    setEditBusy(true);
    setError(null);
    try {
      await api.patch(`/super-admin/clubs/${clubId}`, editForm);
      setEditOpen(false);
      reload();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setEditBusy(false);
    }
  };

  const resendInvite = async (userId) => {
    setResendingId(userId);
    setError(null);
    try {
      const { data: r } = await api.post(`/super-admin/clubs/${clubId}/resend-invite/${userId}`);
      if (r.already_activated) {
        setError("That member has already activated — no invite needed.");
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setResendingId(null);
    }
  };

  const toggleStatus = async () => {
    if (!data?.club) return;
    const active = data.club.status === "active";
    const verb = active ? "deactivate" : "reactivate";
    if (!window.confirm(`${active ? "Deactivate" : "Reactivate"} ${data.club.name}?`)) return;
    setStatusBusy(true);
    try {
      await api.post(`/super-admin/clubs/${clubId}/${verb}`);
      reload();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setStatusBusy(false);
    }
  };

  // Type-the-slug delete — irreversible. Wipes orders/wishlists/photos/audit
  // and (carefully) members. Backend snapshots the club into deleted_clubs_log
  // before purging so even a deliberate-delete is forensically recoverable.
  const deleteClub = async () => {
    if (!data?.club) return;
    const slug = data.club.slug || "";
    const typed = window.prompt(
      `PERMANENT DELETE — this wipes ${data.club.name}, every order, wishlist, photo, audit log, and solo-club member account.\n\n` +
      `Type the club slug "${slug}" to confirm:`,
    );
    if (typed === null) return;
    if (typed.trim().toLowerCase() !== slug.toLowerCase()) {
      window.alert("Slug doesn't match. Nothing was deleted.");
      return;
    }
    setStatusBusy(true);
    try {
      const { data: r } = await api.delete(`/super-admin/clubs/${clubId}`, { params: { confirm: slug } });
      window.alert(`${data.club.name} deleted.\n\nPurges: ${JSON.stringify(r.purges)}`);
      navigate("/super-admin/clubs", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setStatusBusy(false);
    }
  };

  // Platform Administrator hard-deletes a member globally across every club.
  // Same type-the-confirmation pattern as the club delete to prevent slips.
  const [deletingMemberId, setDeletingMemberId] = useState(null);
  const deleteMember = async (m) => {
    const email = (m.email || "").trim();
    if (!email) return;
    const typed = window.prompt(
      `GLOBAL HARD DELETE — wipes ${m.name || email} across every club, including all of their orders, wishlist entries, and uploaded photos.\n\n` +
      `Type the member's email "${email}" to confirm:`,
    );
    if (typed === null) return;
    if (typed.trim().toLowerCase() !== email.toLowerCase()) {
      window.alert("Email doesn't match. Nothing was deleted.");
      return;
    }
    setDeletingMemberId(m.user_id);
    try {
      const { data: r } = await api.delete(`/super-admin/members/${m.user_id}`, { params: { confirm: email.toLowerCase() } });
      window.alert(`${m.name || email} deleted.\n\nPurges: ${JSON.stringify(r.counts)}`);
      reload();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setDeletingMemberId(null);
    }
  };

  const intel = data?.intelligence;

  // Platform Administrator nukes the entire member roster for THIS club.
  // Soft-deletes their orders for this club (5-day trash retention), wipes
  // wishlist + photos for this club, pulls the clubs[] entry, and fully
  // deletes any solo-club account. Members of other clubs survive — they
  // just lose access to this one. Type the SHOUTED phrase to confirm.
  const [purgingRoster, setPurgingRoster] = useState(false);
  const purgeRoster = async () => {
    if (!data?.club) return;
    const headcount = (data.members || []).length;
    if (headcount === 0) {
      window.alert("This club already has zero members.");
      return;
    }
    const typed = window.prompt(
      `PURGE ROSTER — wipes ALL ${headcount} member${headcount === 1 ? "" : "s"} ` +
      `from ${data.club.name}, including their orders (5-day trash), wishlist, and uploaded photos.\n\n` +
      `Members of other clubs will keep those accounts; solo-club members are fully deleted.\n\n` +
      `Type DELETE-ALL-MEMBERS to confirm:`,
    );
    if (typed === null) return;
    if (typed.trim() !== "DELETE-ALL-MEMBERS") {
      window.alert("Phrase doesn't match. Nothing was purged.");
      return;
    }
    setPurgingRoster(true);
    try {
      const { data: r } = await api.post(
        `/super-admin/clubs/${clubId}/purge-members`,
        { confirm: "DELETE-ALL-MEMBERS" },
      );
      window.alert(`Roster purged.\n\n${JSON.stringify(r.counts, null, 2)}`);
      reload();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setPurgingRoster(false);
    }
  };
  const orderStatusEntries = useMemo(() => {
    if (!intel?.status_breakdown) return [];
    return Object.entries(intel.status_breakdown).sort((a, b) => b[1] - a[1]);
  }, [intel]);

  return (
    <div className="bg-cream min-h-screen" data-testid="sa-club-detail-page">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-10">
        <Link
          to="/super-admin/clubs"
          className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] text-[#1A422D] hover:underline"
          data-testid="sa-club-detail-back"
        >
          <ChevronLeft size={14} /> Back to Clubs
        </Link>

        {loading && <div className="mt-10 text-[#7A7A7A]">Loading…</div>}
        {error && (
          <div className="mt-6 px-4 py-3 text-sm border border-[#A33] bg-[#fbe9e9] text-[#8a1a1a]" data-testid="sa-club-detail-error">
            {error}
          </div>
        )}

        {data && data.club && (
          <>
            {/* Header */}
            <div className="mt-4 flex items-end justify-between gap-6 flex-wrap">
              <div>
                <div className="overline">Tenant detail</div>
                <h1 className="font-serif text-4xl sm:text-5xl text-[#1A1A1A] mt-1 leading-none">{data.club.name}</h1>
                <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-[#4A4A4A]">
                  <span className="font-mono">{data.club.slug}</span>
                  <span>·</span>
                  <span>{data.club.city || "—"}</span>
                  <span>·</span>
                  <span
                    className={`px-2.5 py-1 text-[10px] uppercase tracking-[0.18em] border ${
                      data.club.status === "active"
                        ? "border-[#1A422D] bg-[#E9F0EB] text-[#1A422D]"
                        : "border-[#B59A5A] bg-[#FBF6E8] text-[#8a6f33]"
                    }`}
                    data-testid="sa-club-status"
                  >
                    {data.club.status === "active" ? "Active" : "Deactivated"}
                  </span>
                  {data.club.plan && (
                    <>
                      <span>·</span>
                      <PlanBadge plan={data.club.plan} />
                    </>
                  )}
                  <span className="text-xs text-[#7A7A7A] font-mono">{data.club.club_id}</span>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => { setRotateOpen((v) => !v); setRotateResult(null); }}
                  data-testid="sa-club-rotate-toggle"
                  className="btn-secondary inline-flex items-center gap-2"
                >
                  <KeyRound size={14} /> Rotate Passcode
                </button>
                <button
                  type="button"
                  onClick={() => setEditOpen((v) => !v)}
                  data-testid="sa-club-edit-toggle"
                  className="btn-secondary inline-flex items-center gap-2"
                >
                  Edit Profile
                </button>
                <button
                  type="button"
                  onClick={toggleStatus}
                  disabled={statusBusy}
                  data-testid="sa-club-status-toggle"
                  className="btn-secondary inline-flex items-center gap-2"
                >
                  {data.club.status === "active"
                    ? <><ShieldOff size={14} /> Deactivate</>
                    : <><ShieldCheck size={14} /> Reactivate</>}
                </button>
                <button
                  type="button"
                  onClick={deleteClub}
                  disabled={statusBusy}
                  data-testid="sa-club-delete"
                  title={`Permanently delete ${data.club.name} and all its data`}
                  className="inline-flex items-center gap-2 px-4 py-2.5 text-xs uppercase tracking-[0.18em] font-semibold border border-[#8a1a1a] text-[#8a1a1a] hover:bg-[#fbe9e9] transition-colors"
                >
                  <Trash2 size={14} /> Delete
                </button>
              </div>
            </div>

            {/* Rotate Passcode panel */}
            {rotateOpen && (
              <section className="mt-6 surface-card p-6 sm:p-8" data-testid="sa-club-rotate-panel">
                <div className="overline">Reset Back Office Passcode</div>
                <h2 className="font-serif text-2xl mt-1">Set a New Permanent Passcode</h2>
                <p className="text-sm text-[#4A4A4A] mt-2 max-w-2xl">
                  This replaces the club&rsquo;s existing back-office passcode. You&rsquo;ll only see the new value once, on this screen &mdash; copy it before navigating away. Master passcode (1122334455) keeps working independently.
                </p>
                {rotateResult ? (
                  <div className="mt-5 border border-[#1A422D] bg-[#E9F0EB] p-5">
                    <div className="overline" style={{ color: "#1A422D" }}>New Passcode</div>
                    <div className="mt-2 font-mono text-2xl text-[#1A422D] tracking-[0.25em]" data-testid="sa-club-rotate-result">{rotateResult}</div>
                    <p className="text-xs text-[#4A4A4A] mt-2">Send this to the club&rsquo;s pro shop via a secure channel.</p>
                  </div>
                ) : (
                  <div className="mt-5 flex items-end gap-3 flex-wrap">
                    <div className="flex-1 min-w-[200px]">
                      <label className="label-elegant">New passcode (5–10 chars)</label>
                      <input
                        type="text"
                        value={newPasscode}
                        onChange={(e) => setNewPasscode(e.target.value)}
                        maxLength={10}
                        className="input-field mt-1.5 font-mono tracking-[0.18em]"
                        placeholder="e.g. cypress01"
                        data-testid="sa-club-rotate-input"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={rotatePasscode}
                      disabled={rotateBusy || newPasscode.length < 5}
                      className="btn-primary"
                      data-testid="sa-club-rotate-submit"
                    >
                      {rotateBusy ? "Rotating…" : "Rotate"}
                    </button>
                  </div>
                )}
              </section>
            )}

            {/* Edit profile panel */}
            {editOpen && (
              <section className="mt-6 surface-card p-6 sm:p-8" data-testid="sa-club-edit-panel">
                <div className="overline">Edit Profile</div>
                <h2 className="font-serif text-2xl mt-1">Club Details</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-5">
                  <Field label="Club name" value={editForm.name} onChange={(v) => setEditForm({ ...editForm, name: v })} testId="sa-club-edit-name" />
                  <Field label="City" value={editForm.city} onChange={(v) => setEditForm({ ...editForm, city: v })} testId="sa-club-edit-city" />
                  <Field label="Contact email" value={editForm.contact_email} onChange={(v) => setEditForm({ ...editForm, contact_email: v })} testId="sa-club-edit-email" />
                  <Field label="Phone" value={editForm.phone} onChange={(v) => setEditForm({ ...editForm, phone: v })} testId="sa-club-edit-phone" />
                  <Field label="Address" value={editForm.address} onChange={(v) => setEditForm({ ...editForm, address: v })} testId="sa-club-edit-address" />
                  <Field label="Website" value={editForm.website} onChange={(v) => setEditForm({ ...editForm, website: v })} testId="sa-club-edit-website" />
                  <Field label="Established" value={editForm.established_year} onChange={(v) => setEditForm({ ...editForm, established_year: v })} testId="sa-club-edit-year" />
                </div>
                <div className="mt-6 space-y-4 border-t border-[#E5E3D8] pt-5">
                  {/* Self-service join settings — these change how new members
                      who hit /join/<slug> are handled. */}
                  <label className="flex items-start gap-3 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={!!editForm.auto_approve_members}
                      onChange={(e) => setEditForm({ ...editForm, auto_approve_members: e.target.checked })}
                      data-testid="sa-club-edit-auto-approve"
                      className="accent-[#1A422D] w-4 h-4 mt-0.5"
                    />
                    <div>
                      <div className="text-sm text-[#1A1A1A] font-semibold">Auto-approve new members</div>
                      <div className="text-xs text-[#7A7A7A] mt-0.5">
                        Anyone who signs up via the public <span className="font-mono">/join/{data.club.slug}</span> link is immediately active. Leave unchecked to send signups to the Pending Approvals queue.
                      </div>
                    </div>
                  </label>
                  <div>
                    <label className="label-elegant block mb-1.5">Public &ldquo;about&rdquo; for the join page</label>
                    <textarea
                      rows={3}
                      maxLength={600}
                      value={editForm.public_about || ""}
                      onChange={(e) => setEditForm({ ...editForm, public_about: e.target.value })}
                      data-testid="sa-club-edit-public-about"
                      placeholder="A short paragraph members see on the public invite page."
                      className="input-field w-full"
                    />
                    <p className="text-[10px] text-[#7A7A7A] mt-1">{(editForm.public_about || "").length}/600</p>
                  </div>
                </div>
                <div className="mt-5 flex justify-end gap-3">
                  <button type="button" onClick={() => setEditOpen(false)} className="btn-secondary text-xs">Cancel</button>
                  <button type="button" onClick={saveProfile} disabled={editBusy} className="btn-primary" data-testid="sa-club-edit-save">
                    {editBusy ? "Saving…" : "Save Changes"}
                  </button>
                </div>
              </section>
            )}

            {/* Intelligence summary cards */}
            <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard icon={<Users size={16} />} label="Members" value={intel?.member_count ?? 0}
                hint={intel ? `${intel.activated_member_count} activated · ${intel.pending_invite_count} pending` : ""}
                testId="sa-club-stat-members" />
              <StatCard icon={<Package size={16} />} label="Active Orders" value={intel?.active_order_count ?? 0} testId="sa-club-stat-orders" />
              <StatCard icon={<Bookmark size={16} />} label="Wishlist" value={intel?.wishlist_count ?? 0} testId="sa-club-stat-wishlist" />
              <StatCard icon={<Clock size={16} />} label="Onboarded" value={(data.club.created_at || "").slice(0, 10) || "—"} testId="sa-club-stat-onboarded" />
            </div>

            {/* Tabs */}
            <div className="mt-10 border-b border-[#E5E3D8] flex flex-wrap gap-1 sm:gap-6">
              <TabBtn id="profile" active={tab} onClick={setTab} testId="sa-tab-profile">Profile</TabBtn>
              <TabBtn id="intel" active={tab} onClick={setTab} testId="sa-tab-intel">Intelligence</TabBtn>
              <TabBtn id="members" active={tab} onClick={setTab} testId="sa-tab-members">Members · {data.members.length}</TabBtn>
              <TabBtn id="orders" active={tab} onClick={setTab} testId="sa-tab-orders">Recent Orders · {data.recent_orders.length}</TabBtn>
            </div>

            {/* Tab contents */}
            {tab === "profile" && (
              <section className="mt-6 surface-card p-6 sm:p-8" data-testid="sa-pane-profile">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-y-4 gap-x-10 text-sm">
                  <DetailRow label="Club name" value={data.club.name} />
                  <DetailRow label="Slug" value={data.club.slug} mono />
                  <DetailRow label="Club ID" value={data.club.club_id} mono />
                  <DetailRow label="Status" value={data.club.status} />
                  <DetailRow label="Contact email" value={data.club.contact_email || "—"} />
                  <DetailRow label="Phone" value={data.club.phone || "—"} />
                  <DetailRow label="Address" value={data.club.address || "—"} />
                  <DetailRow label="City" value={data.club.city || "—"} />
                  <DetailRow label="Website" value={data.club.website || "—"} />
                  <DetailRow label="Established" value={data.club.established_year || "—"} />
                  <DetailRow label="Created at" value={(data.club.created_at || "").slice(0, 19).replace("T", " · ")} />
                  <DetailRow label="Back-office passcode" value={data.club.has_back_office_passcode ? "Configured · hashed (cannot reveal)" : "— Not set"} />
                </div>
              </section>
            )}

            {tab === "intel" && (
              <section className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="sa-pane-intel">
                <RankCard title="Top Brands" rows={intel?.top_brands || []} emptyHint="No orders yet for this club." testId="sa-intel-brands" />
                <RankCard title="Top Categories" rows={intel?.top_categories || []} emptyHint="No orders yet for this club." testId="sa-intel-categories" />
                <div className="surface-card p-6" data-testid="sa-intel-status">
                  <div className="overline">Order Status Breakdown</div>
                  {orderStatusEntries.length === 0 ? (
                    <p className="text-sm text-[#7A7A7A] mt-3">No orders yet for this club.</p>
                  ) : (
                    <ul className="mt-3 space-y-2">
                      {orderStatusEntries.map(([status, n]) => (
                        <li key={status} className="flex items-center justify-between text-sm">
                          <span className="capitalize">{status.replace(/_/g, " ")}</span>
                          <span className="font-mono text-[#1A422D]">{n}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </section>
            )}

            {tab === "members" && (
              <section className="mt-6" data-testid="sa-pane-members">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-xs uppercase tracking-[0.18em] text-[#7A7A7A]">
                    {data.members.length} member{data.members.length === 1 ? "" : "s"}
                  </div>
                  <button
                    type="button"
                    onClick={purgeRoster}
                    disabled={purgingRoster || data.members.length === 0}
                    data-testid="sa-purge-roster-btn"
                    className="inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-[0.18em] font-semibold bg-[#8a1a1a] hover:bg-[#A33] text-[#F9F8F4] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    title="Wipe every member from this club"
                  >
                    <Trash2 size={12} />
                    {purgingRoster ? "Purging…" : "Purge roster"}
                  </button>
                </div>
                <div className="surface-card overflow-x-auto">
                <table className="w-full text-left">
                  <thead className="bg-[#F3F1E9]">
                    <tr className="text-xs uppercase tracking-wider text-[#4A4A4A]">
                      <th className="px-6 py-4">Member</th>
                      <th className="px-6 py-4">Email</th>
                      <th className="px-6 py-4">Member #</th>
                      <th className="px-6 py-4">Member ID</th>
                      <th className="px-6 py-4">Joined</th>
                      <th className="px-6 py-4">Activation</th>
                      <th className="px-6 py-4 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.members.length === 0 && (
                      <tr><td colSpan={7} className="px-6 py-10 text-center text-[#7A7A7A]">No members yet &mdash; invite a roster from the club&rsquo;s back office.</td></tr>
                    )}
                    {data.members.map((m) => (
                      <tr key={m.user_id} className="border-t border-[#E5E3D8]" data-testid={`sa-member-row-${m.user_id}`}>
                        <td className="px-6 py-4">
                          <div className="font-serif text-base text-[#1A1A1A]">{m.name || "—"}</div>
                          <div className="text-xs text-[#7A7A7A]">{m.membership_category || "—"}</div>
                        </td>
                        <td className="px-6 py-4 text-sm">{m.email}</td>
                        <td className="px-6 py-4 text-sm font-mono">{m.member_number || "—"}</td>
                        <td className="px-6 py-4">
                          <IdChip
                            value={m.user_id}
                            size="xs"
                            testId={`sa-member-id-${m.user_id}`}
                          />
                        </td>
                        <td className="px-6 py-4 text-xs text-[#7A7A7A]">{(m.created_at || "").slice(0, 10)}</td>
                        <td className="px-6 py-4">
                          {m.activated ? (
                            <span className="inline-flex items-center gap-1.5 text-xs text-[#1A422D]"><CheckCircle2 size={12} /> Activated</span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 text-xs text-[#8a6f33]"><Mail size={12} /> Pending invite</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="inline-flex items-center gap-3 justify-end">
                            {!m.activated && (
                              <button
                                type="button"
                                onClick={() => resendInvite(m.user_id)}
                                disabled={resendingId === m.user_id}
                                data-testid={`sa-member-resend-${m.user_id}`}
                                className="text-xs text-[#1A422D] underline underline-offset-4 inline-flex items-center gap-1.5"
                              >
                                <RefreshCw size={12} />
                                {resendingId === m.user_id ? "Sending…" : "Resend invite"}
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => deleteMember(m)}
                              disabled={deletingMemberId === m.user_id}
                              data-testid={`sa-member-delete-${m.user_id}`}
                              className="text-xs text-[#8a1a1a] hover:underline underline-offset-4 inline-flex items-center gap-1.5"
                              title="Delete this member globally (across every club)"
                            >
                              <Trash2 size={12} />
                              {deletingMemberId === m.user_id ? "Deleting…" : "Delete"}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              </section>
            )}

            {tab === "orders" && (
              <section className="mt-6 surface-card overflow-x-auto" data-testid="sa-pane-orders">
                <table className="w-full text-left">
                  <thead className="bg-[#F3F1E9]">
                    <tr className="text-xs uppercase tracking-wider text-[#4A4A4A]">
                      <th className="px-6 py-4">Order</th>
                      <th className="px-6 py-4">Item</th>
                      <th className="px-6 py-4">Member</th>
                      <th className="px-6 py-4">Brand / Category</th>
                      <th className="px-6 py-4">Status</th>
                      <th className="px-6 py-4">Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.recent_orders.length === 0 && (
                      <tr><td colSpan={6} className="px-6 py-10 text-center text-[#7A7A7A]">No orders yet for this club.</td></tr>
                    )}
                    {data.recent_orders.map((o) => (
                      <tr key={o.order_id} className="border-t border-[#E5E3D8]">
                        <td className="px-6 py-4 text-sm font-mono text-[#1A422D]">
                          <Link to={`/orders/${o.order_id}`} className="hover:underline inline-flex items-center gap-1">
                            {o.order_id} <ChevronRight size={12} />
                          </Link>
                          {o.batch_id && <div className="text-[10px] text-[#7A7A7A] font-mono mt-0.5">batch {o.batch_id.slice(-8)}</div>}
                        </td>
                        <td className="px-6 py-4 text-sm">
                          <div>{o.item_name || "—"}</div>
                          <div className="text-xs text-[#7A7A7A]">{o.category || "—"} · qty {o.quantity || 1}</div>
                        </td>
                        <td className="px-6 py-4 text-sm">
                          <div>{o.member_name || "—"}</div>
                          <div className="text-xs text-[#7A7A7A]">{o.member_email}</div>
                        </td>
                        <td className="px-6 py-4 text-sm">
                          <div>{o.brand || "—"}</div>
                          <div className="text-xs text-[#7A7A7A]">{o.category || ""}</div>
                        </td>
                        <td className="px-6 py-4 text-sm capitalize">{(o.status || "").replace(/_/g, " ")}</td>
                        <td className="px-6 py-4 text-xs text-[#7A7A7A]">{(o.created_at || "").slice(0, 10)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ----- small presentational components below (kept local; not reused elsewhere) -----

function TabBtn({ id, active, onClick, children, testId }) {
  const isActive = active === id;
  return (
    <button
      type="button"
      onClick={() => onClick(id)}
      data-testid={testId}
      className={`px-3 py-3 text-sm uppercase tracking-[0.18em] border-b-2 -mb-px transition-colors ${
        isActive ? "border-[#1A422D] text-[#1A422D]" : "border-transparent text-[#7A7A7A] hover:text-[#1A422D]"
      }`}
    >
      {children}
    </button>
  );
}

function StatCard({ icon, label, value, hint, testId }) {
  return (
    <div className="surface-card p-5" data-testid={testId}>
      <div className="flex items-center justify-between text-[#4A4A4A]">
        <span className="overline">{label}</span>
        <span className="text-[#1A422D]">{icon}</span>
      </div>
      <div className="font-serif text-3xl text-[#1A1A1A] mt-1">{value}</div>
      {hint && <div className="text-xs text-[#7A7A7A] mt-1">{hint}</div>}
    </div>
  );
}

function DetailRow({ label, value, mono }) {
  return (
    <div className="flex items-baseline gap-4">
      <span className="overline w-40 shrink-0">{label}</span>
      <span className={`text-[#1A1A1A] ${mono ? "font-mono text-[#1A422D]" : ""}`}>{value || "—"}</span>
    </div>
  );
}

function RankCard({ title, rows, emptyHint, testId }) {
  return (
    <div className="surface-card p-6" data-testid={testId}>
      <div className="overline">{title}</div>
      {rows.length === 0 ? (
        <p className="text-sm text-[#7A7A7A] mt-3">{emptyHint}</p>
      ) : (
        <ul className="mt-3 space-y-2">
          {rows.map((r) => (
            <li key={r.value} className="flex items-center justify-between text-sm">
              <span className="text-[#1A1A1A]">{r.value}</span>
              <span className="font-mono text-[#1A422D]">{r.count}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Field({ label, value, onChange, testId }) {
  return (
    <div>
      <label className="label-elegant">{label}</label>
      <input
        type="text"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="input-field w-full mt-1.5"
      />
    </div>
  );
}
